import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type TabNavLink, TabNavService } from '@big-direkt/a11y';
import { MobileWizardComponent, PageNavigationService } from '@big-direkt/form/view';
import { SearchFormComponent } from '@big-direkt/search';
import { UserService } from '@big-direkt/state/user';
import { UiHeaderComponent } from '@big-direkt/ui/header';
import { UiLinkComponent } from '@big-direkt/ui/link';
import { EnvironmentService } from '@big-direkt/utils/environment';
import { IconBigMediumBenutzer, IconBigMediumBurgerMenu, IconBigMediumSuche, IconBigMediumTelefon24H, IconComponent } from '@big-direkt/utils/icons';
import { BreakpointService, MenuItemType } from '@big-direkt/utils/shared';
import { translateObjectSignal } from '@jsverse/transloco';
import { map } from 'rxjs';
import { NavService } from '../../services/nav.service';
import { SidenavService } from '../../services/sidenav.service';
import { NavMainDesktopComponent } from '../nav-main-desktop/nav-main-desktop.component';
import { NavQuicknavComponent } from '../nav-quicknav/nav-quicknav.component';
import { NavUserButtonComponent } from '../nav-user/nav-user-button.component';
import { HeaderContext } from './../../models/header-context.model';
import { NavMobileIconButtonComponent } from './nav-mobile-icon-button.component';

@Component({
    selector: 'big-nav-header',
    templateUrl: './nav-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '(window:scroll)': 'onScroll()',
    },
    imports: [
        IconComponent,
        NavQuicknavComponent,
        NavMainDesktopComponent,
        NavUserButtonComponent,
        UiLinkComponent,
        UiHeaderComponent,
        MobileWizardComponent,
        SearchFormComponent,
        NavMobileIconButtonComponent,
    ],
})
export class NavHeaderComponent {
    private readonly a11yTabNavService = inject(TabNavService);
    private readonly breakpointService = inject(BreakpointService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly navService = inject(NavService);
    private readonly pageNavigationService = inject(PageNavigationService);
    private readonly sidenavService = inject(SidenavService);
    private readonly userService = inject(UserService);

    private readonly menuItemTypeContact = MenuItemType.Contact;

    public readonly a11yTabNavTranslate = translateObjectSignal('a11yTabNav');
    public readonly navTranslate = translateObjectSignal('nav');
    public readonly desktopHeaderToolsLink = computed<TabNavLink>(() => {
        const translate = this.a11yTabNavTranslate();

        return {
            title: translate.headerToolsLink.title,
            key: 'header-tools-desktop',
            displayValue: translate.headerToolsLink.displayValue,
            viewPort: 'desktop',
            order: 5,
        };
    });

    public readonly mobileContactLink = computed<TabNavLink>(() => {
        const translate = this.a11yTabNavTranslate();

        return {
            title: translate.contactLink.title,
            key: 'contact-mobile',
            displayValue: translate.contactLink.displayValue,
            viewPort: 'mobile',
            order: 5,
        };
    });

    public readonly desktopContactLink = computed<TabNavLink>(() => ({
        ...this.mobileContactLink(),
        key: 'contact-desktop',
        viewPort: 'desktop',
    }));

    public readonly mobileMenuLink = computed<TabNavLink>(() => {
        const translate = this.a11yTabNavTranslate();

        return {
            title: translate.menuLink.title,
            key: 'menu-mobile',
            displayValue: translate.menuLink.displayValue,
            viewPort: 'mobile',
            order: 10,
        };
    });

    public readonly desktopMenuLink = computed<TabNavLink>(() => ({
        ...this.mobileMenuLink(),
        key: 'menu-desktop',
        viewPort: 'desktop',
    }));

    public readonly iconMenu = IconBigMediumBurgerMenu;
    public readonly iconPhone = IconBigMediumTelefon24H;
    public readonly iconSearch = IconBigMediumSuche;
    public readonly iconUser = IconBigMediumBenutzer;
    public readonly initials = toSignal(this.userService.getProfile().pipe(map(profile => profile?.name.initials)));
    public readonly isUserLoggedIn = this.userService.isAuthenticated;
    public readonly menus = this.navService.headerMenus;
    public readonly privatePath = this.environmentService.privatePath;
    public readonly servicePhoneNumber = this.environmentService.servicePhoneNumber;

    public readonly centeredTitle = input<string>();
    public readonly showSearch = input<boolean>(true);
    public readonly headerContext = input.required<HeaderContext>();

    public readonly contactUri = computed(() => {
        const uri = this.menus()?.top?.items.find(item => item.type === this.menuItemTypeContact)?.uri;

        return uri ?? '/de/kontakt';
    });
    public readonly isSidenavOpen = computed(() => !!this.sidenavService.currentSidenav());
    public readonly isWizardVisible = computed(() => !!this.pageNavigationService.getCurrentPageSignal()()?.isWizardVisible);

    public constructor() {
        effect(() => {
            this.a11yTabNavService.registerLinks(
                this.desktopHeaderToolsLink(),
                this.mobileContactLink(),
                this.desktopContactLink(),
                this.mobileMenuLink(),
                this.desktopMenuLink(),
            );
        });
    }

    public closeMobileNav(): void {
        this.sidenavService.closeSidenav();
    }

    public onScroll(): void {
        this.breakpointService.scrollPositionY();
    }
}
